import React, { useContext } from "react";
import { t } from "ttag";

import type { MainNavBlock } from "@reactivated";

import { ReactivatedContext } from "../../context";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { useHeaderContext } from "./HeaderContext";
import { SEARCH_SUBMENU } from "./SearchSubmenu";
import { useMainMenuKeyboardNav } from "./hooks/useMainMenuKeyboardNav";

import styles from "./MainMenuItem.module.scss";

type MainNavItem = MainNavBlock[number];

const LinkItem = ({
    item,
}: {
    item: Extract<MainNavItem, { type: "link" }>;
}) => {
    const { setActiveSubmenu } = useHeaderContext();
    const { onKeyDown } = useMainMenuKeyboardNav();
    const { request } = useContext(ReactivatedContext);

    return (
        <Clickable
            simpleLink={item.value}
            className={styles.link}
            onMouseEnter={() => setActiveSubmenu(null)}
            onKeyDown={onKeyDown}
            data-menu-trigger // see useMainMenuKeyboardNav
            aria-current={item.value.url === request.path ? "page" : undefined}
        />
    );
};

export const SubmenuTrigger = ({
    menuId,
    title,
}: {
    menuId: string;
    title?: string;
}) => {
    const { activeSubmenu, setActiveSubmenu } = useHeaderContext();
    const isOpen = activeSubmenu === menuId;
    const isSearch = menuId === SEARCH_SUBMENU;
    const { onKeyDown } = useMainMenuKeyboardNav(isOpen ? menuId : undefined);

    const className = isOpen
        ? styles.submenuTriggerActive
        : styles.submenuTrigger;
    const searchClassName = styles.searchTrigger;

    return (
        <Clickable
            className={isSearch ? searchClassName : className}
            aria-expanded={isOpen}
            aria-controls={menuId}
            onPress={(e) => {
                /* v8 ignore if -- @preserve */
                if (e.pointerType === "mouse") {
                    setActiveSubmenu(menuId);
                } else {
                    setActiveSubmenu(isOpen ? null : menuId);
                }
            }}
            onMouseEnter={() => setActiveSubmenu(menuId)}
            onKeyDown={onKeyDown}
            data-menu-trigger // see useMainMenuKeyboardNav
        >
            {isSearch ? (
                <Svg
                    name="search"
                    className={styles.searchTriggerIcon}
                    visuallyHiddenText={t`Search`}
                />
            ) : (
                title
            )}
        </Clickable>
    );
};

interface Props {
    item: MainNavItem;
}

export const MainMenuItem = ({ item }: Props) => {
    switch (item.type) {
        case "divider":
            return <span className={styles.divider} aria-hidden="true" />;
        case "link":
            return <LinkItem item={item} />;
        case "submenu":
            if (!item.value.title) return <></>;
            return <SubmenuTrigger menuId={item.id} title={item.value.title} />;
    }
};
